import type { SerializedHardware } from '@enmon/commons/lib/tools';
import type { ColumnsType, SorterResult } from 'antd/lib/table/interface';
import { Table } from 'components/table/Table';
import type React from 'react';
import { useCallback, useState, type VFC } from 'react';
import { useHistory } from 'react-router-dom';
import { useDeleteHardware } from 'api/hooks';
import { Tag } from 'components';
import { useTheme } from 'styled-components';
import type { HardwaresDataItem } from '../queries/getHardwareDataList';
import { useHardwareListFilterWithPagination } from '../useHardwareListFilterWithPagination.ts';
import { HARDWARE_CATEGORIES_TRANSLATED } from '../../../const';
import { ActionButton } from './button';

interface HardwareListTableProps {
  data: HardwaresDataItem[];
  loading: boolean;
  onSuccess: () => void;
  onEdit: (hardware: SerializedHardware) => void;
  onCopy: (hardware: SerializedHardware) => void;
}

export const HardwareListTable: VFC<HardwareListTableProps> = ({ data, loading, onSuccess, onEdit, onCopy }) => {
  const theme = useTheme();
  const history = useHistory();
  const { setFilterState, pagination } = useHardwareListFilterWithPagination();
  const { mutate: deleteHardware, isLoading: isDeleting } = useDeleteHardware(onSuccess);

  const handleTableChange = (sorter: SorterResult<HardwaresDataItem>) => {
    const sorterField = Array.isArray(sorter.field) ? sorter.field.slice(-1) : sorter.field;

    setFilterState({
      ...(sorter.order &&
        sorterField && {
          sort: `${sorterField as string}:${sorter.order.slice(0, -3)}`,
        }),
    });
  };

  const handleDelete = useCallback(
    (id: SerializedHardware['id']) => {
      deleteHardware(id);
    },
    [deleteHardware],
  );

  const handleClick = useCallback(
    (hardware: SerializedHardware) => {
      history.push(`${history.location.pathname}/${hardware.id}/query-builder`);
    },
    [history],
  );

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const columns: ColumnsType<HardwaresDataItem> = [
    {
      title: 'External ID',
      dataIndex: 'external_id',
      key: 'external_id',
      sorter: true,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      sorter: true,
      render: (_, value) => <>{HARDWARE_CATEGORIES_TRANSLATED[value.category] ?? value.category}</>,
    },
    {
      title: 'Disabled',
      dataIndex: 'disabled',
      key: 'disabled',
      sorter: true,
      render: (_, value) => <Tag>{value.disabled ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      sorter: true,
      render: (_, value) => value.priority,
    },
    {
      title: 'Has HW query',
      dataIndex: 'query',
      key: 'query',
      render: (_, value) => (
        <Tag color={value.query ? theme.color.status.success : theme.color.status.error}>
          {value.query ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Enmon Details',
      dataIndex: 'enmon_details',
      key: 'enmon_details',
      render: (_, value) =>
        value.enmon_details ? (
          <div>
            {Object.entries(value.enmon_details).map(([key, val]) => (
              <div key={key}>
                <strong>{key}:</strong> {typeof val === 'object' ? JSON.stringify(val) : String(val)}
              </div>
            ))}
          </div>
        ) : (
          '—'
        ),
    },
    {
      width: 150,
      title: 'Actions',
      key: 'action',
      fixed: 'right',
      render: (_, hardware) => (
        <ActionButton
          isLoading={loading || isDeleting}
          onClick={() => handleClick(hardware)}
          onCopy={() => onCopy(hardware)}
          onDelete={() => handleDelete(hardware.id)}
          onEdit={() => onEdit(hardware)}
        />
      ),
    },
  ];

  return (
    <Table<HardwaresDataItem>
      columns={columns}
      dataSource={data}
      loading={loading}
      pagination={{ ...pagination, showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} Hardwares` }}
      rowKey={item => item.id}
      rowSelection={rowSelection}
      scroll={{ x: 'fit-content' }}
      onChange={(_, __, sorter) => handleTableChange(Array.isArray(sorter) ? sorter[0] : sorter)}
    />
  );
};
